import React, { useContext, useRef } from "react";
import { Button, Tooltip } from "@mui/material";
import * as XLSX from "xlsx";
import { DatasheetContext } from "../datasheet.context";
import TableViewIcon from "@mui/icons-material/TableView";


const ExportImportExcel = ({
  tableId,
  tableName,
  datasheetId,
  tableIndex,
  setReadingRowsLocal,
  table, 
  UNCERTAINTY_LINK,
  EXTRA_COLUMN_LINK,
  MAX_COLS,
}) => {
  const datasheetContextValue = useContext(DatasheetContext);
  const {
    state: { staticReadingRows },
    dispatch,
  } = datasheetContextValue;
  const fileInputRef = useRef(null);

  // ✅ Build key -> header map (excludes uncertainty columns)
  const buildKeyToHeaderMap = () => {
    const map = {};
    Array.from({ length: MAX_COLS + 1 }, (_, i) => {
      const columnKey = "c" + i;
      if (table[columnKey] != null) {
        const rawHeader = table[columnKey];

        // ✅ Skip uncertainty columns entirely
        if (rawHeader?.includes(UNCERTAINTY_LINK)) return;

        let headerName = rawHeader;
        if (headerName?.includes(EXTRA_COLUMN_LINK)) {
          headerName = headerName.split(EXTRA_COLUMN_LINK)?.[0];
        }
        map[columnKey] = headerName;
      }
    });
    return map;
  };

  // ✅ Build header -> key map  e.g { "Temperature": "c1", "Pressure": "c2" }
  const buildHeaderToKeyMap = () => {
    const keyToHeader = buildKeyToHeaderMap();
    const map = {};
    Object.entries(keyToHeader).forEach(([key, header]) => {
      map[header] = key;
    });
    return map;
  };

  // ✅ Build common header row (merged cells)
  const buildCommonHeaderRows = () => {
    const defaultConfiguration = table?.defaultConfiguration;
    if (!defaultConfiguration?.hasCommonHeader || !defaultConfiguration?.commonHeaderConfig) {
      return null;
    }

    const commonHeaderConfig = defaultConfiguration.commonHeaderConfig.split(",");
    const commonHeaderRow = [];

    commonHeaderConfig.forEach((col) => {
      let [name, size] = col?.split(":");
      const colSpan = Number(size);
      for (let i = 0; i < colSpan; i++) {
        commonHeaderRow.push(name !== "$" ? name : "");
      }
    });

    return commonHeaderRow;
  };

  const handleExport = () => {
    const keyToHeader = buildKeyToHeaderMap();
    const commonHeaderRow = buildCommonHeaderRows();

    // Filter rows matching the tableId
    const dataToExport = staticReadingRows
      ?.filter((row) => row.tableId === tableId)
      ?.map(({ id, datasheetId, tableId, ...rest }) => {
        const mappedRow = {};
        Object.entries(rest).forEach(([key, value]) => {
          // ✅ Skip keys not in header map (unmapped/extra keys excluded)
          if (!keyToHeader[key]) return;

          const rawHeader = table[key];
          // ✅ Skip uncertainty columns
          if (rawHeader?.includes(UNCERTAINTY_LINK)) return;

          const headerName = keyToHeader[key];
          mappedRow[headerName] = value;
        });
        return mappedRow;
      });

    const workbook = XLSX.utils.book_new();

    if (commonHeaderRow && dataToExport.length > 0) {
      // ✅ Get column headers (second row)
      const columnHeaders = Object.keys(dataToExport[0]);

      // ✅ Build sheet with common header as first row
      const worksheetData = [
        commonHeaderRow,           // Row 1: common header
        columnHeaders,             // Row 2: column headers
        ...dataToExport.map((row) => columnHeaders.map((h) => row[h])), // Data rows
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, `T${tableIndex + 1}`);
    } else {
      // No common header, just normal export
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      XLSX.utils.book_append_sheet(workbook, worksheet, `T${tableIndex + 1}`);
    }

    XLSX.writeFile(workbook, `${datasheetId}_${tableId}_T${tableIndex + 1}.xlsx`);
  };

const handleImport = (event) => {
  const file = event.target.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert sheet to array of arrays
        const sheetArray = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const defaultConfiguration = table?.defaultConfiguration;
        const hasCommonHeader = defaultConfiguration?.hasCommonHeader;

        //  If common header exists, headers are on row index 1, else row index 0
        const headerRowIndex = hasCommonHeader ? 1 : 0;
        const dataStartIndex = headerRowIndex + 1;

        const headers = sheetArray[headerRowIndex]; // actual column names
        const headerToKey = buildHeaderToKeyMap();

        //  Convert rows using header -> key map (uncertainty columns won't be in map)
        //  Filter out empty rows where all values are null/undefined/empty string
        const importedData = sheetArray
          .slice(dataStartIndex)
          .map((row) => {
            const mappedRow = {};
            headers.forEach((header, index) => {
              //  Skip if header not found in map (uncertainty columns excluded)
              if (!headerToKey[header]) return;
              const key = headerToKey[header];
              const val = row[index];
              // Convert to string, keep null/undefined/empty as empty string
              mappedRow[key] =
                val !== null && val !== undefined && val !== ""
                  ? String(val)
                  : "";
            });
            return mappedRow;
          })
          .filter((row) =>
            Object.values(row).some(
              (val) => val !== null && val !== undefined && val !== ""
            )
          );

        //  Get existing rows for this table to preserve backend-only fields
        const existingRows = staticReadingRows.filter(
          (row) => row.tableId === tableId
        );

        // Add tableId, datasheetId and preserve backend fields by index
        const filteredData = importedData?.map((row, index) => {
          const existingRow = existingRows[index];
          return {
            ...row,
            tableId,
            datasheetId,
            complianceStatus: existingRow?.complianceStatus ?? null,
            standardRanges:  null, //existingRow?.standardRanges ?? null
            supportiveRanges:  null, //existingRow?.supportiveRanges ?? null
            uncertainty: existingRow?.uncertainty ?? null,
          };
        });

        // Keep previous rows + append new imported rows
        const updatedRows = [
          ...staticReadingRows.filter((row) => row.tableId !== tableId),
          ...staticReadingRows.filter((row) => row.tableId === tableId),
          ...filteredData,
        ];


        setReadingRowsLocal(updatedRows);
        dispatch({
          field: "staticReadingRows",
          value: updatedRows,
        });
      } catch (error) {
        console.error("Error parsing Excel file:", error);
      }
    };
    reader.readAsBinaryString(file);
  }
};

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  return (
    <div>
      <Tooltip title="Export Excel" arrow>
        <Button
          variant="text"
          color="primary"
          onClick={handleExport}
          startIcon={<TableViewIcon />}
          style={{ minWidth: "20px", textTransform: "none", fontSize: "13px" }}
        >
          Export Excel
        </Button>
      </Tooltip>
      <Tooltip title="Import Excel" arrow>
        <Button
          variant="text"
          color="primary"
          onClick={triggerFileInput}
          startIcon={<TableViewIcon />}
          style={{ minWidth: "20px", textTransform: "none", fontSize: "13px" }}
        >
          Import Excel
        </Button>
      </Tooltip>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".xlsx, .xls"
        onChange={handleImport}
      />
    </div>
  );
};

export default ExportImportExcel;